import {
  FC,
  PropsWithChildren,
  Suspense,
  lazy,
  useEffect,
  useRef,
  useState
} from 'react';

import { isEmpty } from 'lodash';
import { MdClose, MdAdd } from 'react-icons/md';

import { FileMap } from '@common/types';
import { DittoAlert } from '@elements';
import NoteSingle from '@imgs/notesingle.png';
import {
  Button,
  Box,
  Center,
  Divider,
  ActionIcon,
  Loader,
  Text,
  Modal,
  Group,
  Image,
  Stack
} from '@ui';
import { getMimeInfo } from '@utils/mimetypes';

import classes from '../styles/DocPreview.module.css';

import FileItem from './FileItem';
import ImagePreview from './ImagePreview';
const PdfDocumentPreview = lazy(() => import('./PdfDocumentPreview'));

interface DocPreviewProps {
  showPreview: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onFileRemove: (fileName: string) => void;
  files: FileMap;
  onAddMoreFiles: (files: FileList) => void;
  docType: string;
  maxFiles: number;
  acceptedFileExts: string;
}

type PreviewAreaProps = {
  error?: boolean;
};

const PreviewArea: FC<PropsWithChildren<PreviewAreaProps>> = ({
  children,
  error
}) => {
  return (
    <Box className={error ? classes.previewErrorArea : classes.previewArea}>
      {children}
    </Box>
  );
};

const DocPreview: FC<DocPreviewProps> = ({
  showPreview,
  onClose,
  onSubmit,
  onAddMoreFiles,
  onFileRemove,
  files,
  docType,
  maxFiles = 1,
  acceptedFileExts
}) => {
  // Key should be Original uncleaned file name
  const [protectedFiles, setProtectedFiles] = useState<Record<string, boolean>>({});
  const [previewFileName, setPreviewFileName] = useState([...files.keys()][0] || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (files && !!files.size) {
      const fileNames = [...files.keys()];
      const lastFileName = fileNames[fileNames.length - 1] || '';
      setPreviewFileName(lastFileName);
    }
  }, [files]);

  const openFileBrowser = () => {
    fileInputRef && fileInputRef.current && fileInputRef.current.click();
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onAddMoreFiles(e.target.files);
    }
  };

  const handleFileRemove =
    (cleanedFileName: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const originalFileName = files.get(cleanedFileName)?.name || cleanedFileName;
      // destructure removed filename to throwaway and get remaining protected files and set to new state
      const { [originalFileName]: _throwAway, ...restProtectedFiles } =
        protectedFiles;
      setProtectedFiles(restProtectedFiles);
      onFileRemove(cleanedFileName);
    };

  // Do not allow protected files as they could be rejected
  const handlePasswordProtectedFile = (originalFileName: string) => {
    setProtectedFiles((prevFiles) => ({
      ...prevFiles,
      [originalFileName]: true
    }));
  };

  const handleClose = () => {
    if (!isEmpty(protectedFiles)) {
      setProtectedFiles({});
    }

    onClose();
  };

  const renderFilePreview = () => {
    const previewFile = files.get(previewFileName);
    if (!previewFile)
      return (
        <PreviewArea error>
          <DittoAlert color="danger" message="No files to preview" />
        </PreviewArea>
      );

    const fileInfo = getMimeInfo(previewFile.type);
    if (!fileInfo) return null;

    if (fileInfo.type === 'image') {
      return (
        <PreviewArea>
          <ImagePreview file={previewFile} />
        </PreviewArea>
      );
    }

    if (fileInfo.type === 'document' && fileInfo.mime === 'application/pdf') {
      if (protectedFiles[previewFile.name]) {
        return (
          <PreviewArea error>
            <DittoAlert
              color="danger"
              message="Preview not available, this file is password protected."
            />
          </PreviewArea>
        );
      }

      return (
        <PreviewArea>
          <Suspense
            fallback={
              <Center h={200}>
                <Loader />
              </Center>
            }
          >
            <PdfDocumentPreview
              file={previewFile}
              onPasswordProtectedFile={handlePasswordProtectedFile}
            />
          </Suspense>
        </PreviewArea>
      );
    }

    return (
      <PreviewArea error>
        <DittoAlert color="danger" message="Preview not available" />
      </PreviewArea>
    );
  };

  const hasProtectedFiles = protectedFiles
    ? Object.keys(protectedFiles).length > 0
    : false;

  const canSubmitFileForUpload = files.size > 0 && !hasProtectedFiles;

  return (
    <Modal
      opened={showPreview}
      onClose={handleClose}
      size="xl"
      noContentPadding
      bottomSheet
      centered
    >
      <Box className={classes.previewDialogContainer}>
        <Box className={classes.dialogHeader}>
          <Group align="center" gap={4}>
            <Image src={NoteSingle} w={20} h={24} />
            <Text ml={8} lh={20} fz={20} fw={600}>
              Upload File
            </Text>
          </Group>
          <ActionIcon onClick={onClose} variant="transparent" aria-label="Close">
            <MdClose size={16} />
          </ActionIcon>
        </Box>
        <Box className={classes.docPreviewContainer}>
          <Stack m={24}>
            {renderFilePreview()}
            <Divider my={24} />
            <Box className={classes.fileMetaContainer}>
              {Array.from(files).map(([fileNameKey, fileItem], index) => {
                const isCurrentPreviewFile = previewFileName === fileNameKey;
                const isPasswordProtectedFile = !!protectedFiles[fileNameKey];

                return (
                  <FileItem
                    key={fileNameKey}
                    fileName={fileNameKey}
                    fileItem={fileItem}
                    isPasswordProtectedFile={isPasswordProtectedFile}
                    isActive={isCurrentPreviewFile}
                    onFileClick={() => setPreviewFileName(fileNameKey)}
                    onFileRemove={handleFileRemove}
                    fileCountText={`${index + 1}/${files.size}`}
                  />
                );
              })}
              {files.size < maxFiles && (
                <Box className={classes.addMore}>
                  <input
                    type="file"
                    className={classes.fileInput}
                    ref={fileInputRef}
                    onChange={onFileChange}
                    accept={acceptedFileExts}
                  />
                  <Group onClick={openFileBrowser} align="center" gap={4}>
                    <MdAdd size={16} />
                    <Text fz={14}>{`Add more (${docType})`}</Text>
                  </Group>
                </Box>
              )}
            </Box>
          </Stack>
        </Box>
        <Box className={classes.previewActions}>
          <Button variant="outline" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="filled"
            onClick={onSubmit}
            disabled={!canSubmitFileForUpload}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export { DocPreview };
